import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import "./style.css";
import TextCarousel from "../../components/TextCarousel";

export default function Projects() {
  const carouselTexts = [
    '"To plant a garden is to believe in tomorrow." – Audrey Hepburn',
    '"Look deep into nature, and you will understand everything better." – Albert Einstein',
    '"A flower blossoms for its own joy." – Oscar Wilde',
  ];
  
  const location = useLocation();

  // Meta Tag changes
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);

    const canonicalUrl = `https://bur-han.ca${location.pathname}`;
    let linkTag = document.querySelector('link[rel="canonical"]');
    if (linkTag) {
      linkTag.setAttribute("href", canonicalUrl);
    } else {
      linkTag = document.createElement("link");
      linkTag.setAttribute("rel", "canonical");
      linkTag.setAttribute("href", canonicalUrl);
      document.head.appendChild(linkTag);
    }

    // TITLE - Optimized for portfolio/SEO
    const newTitle = "Landscaping Projects Portfolio Vancouver | BUR-HAN | Before & After";
    document.title = newTitle;

    // META DESCRIPTION - Compelling with keywords
    const newDescription = "Explore BUR-HAN's landscaping projects portfolio in Vancouver. See before & after transformations: 3D design, paving stones, retaining walls, turf installs & plantings. Get inspired for your project!";
    let descriptionMetaTag = document.querySelector('meta[name="description"]');
    if (descriptionMetaTag) {
      descriptionMetaTag.setAttribute("content", newDescription);
    } else {
      descriptionMetaTag = document.createElement("meta");
      descriptionMetaTag.setAttribute("name", "description");
      descriptionMetaTag.setAttribute("content", newDescription);
      document.head.appendChild(descriptionMetaTag);
    }

    // KEYWORDS - Comprehensive portfolio keywords
    const newKeywords = [
      "landscaping projects Vancouver",
      "landscaping portfolio Vancouver",
      "BUR-HAN landscaping projects",
      "landscape design portfolio Vancouver",
      "residential landscaping examples Vancouver",
      "commercial landscape projects BC",
      "before and after landscaping Vancouver",
      "hardscape portfolio Vancouver",
      "softscape portfolio Vancouver",
      "outdoor renovation projects Vancouver",
      "3D landscaping designs Vancouver",
      "paving stone projects Vancouver",
      "retaining wall installations Vancouver",
      "turf installation projects Vancouver",
      "garden plantings Vancouver",
      "landscape transformation Vancouver"
    ].join(", ");
    
    let keywordsMetaTag = document.querySelector('meta[name="keywords"]');
    if (keywordsMetaTag) {
      keywordsMetaTag.setAttribute("content", newKeywords);
    } else {
      keywordsMetaTag = document.createElement("meta");
      keywordsMetaTag.setAttribute("name", "keywords");
      keywordsMetaTag.setAttribute("content", newKeywords);
      document.head.appendChild(keywordsMetaTag);
    }

    // GEO META TAGS - Vancouver specific
    let geoTag = document.querySelector('meta[name="geo.placename"]');
    if (geoTag) {
      geoTag.setAttribute("content", "Vancouver, British Columbia");
    } else {
      geoTag = document.createElement("meta");
      geoTag.setAttribute("name", "geo.placename");
      geoTag.setAttribute("content", "Vancouver, British Columbia");
      document.head.appendChild(geoTag);
    }

    // OPEN GRAPH TAGS
    let ogTitleTag = document.querySelector('meta[property="og:title"]');
    if (ogTitleTag) {
      ogTitleTag.setAttribute("content", newTitle);
    } else {
      ogTitleTag = document.createElement("meta");
      ogTitleTag.setAttribute("property", "og:title");
      ogTitleTag.setAttribute("content", newTitle);
      document.head.appendChild(ogTitleTag);
    }

    let ogDescriptionTag = document.querySelector('meta[property="og:description"]');
    if (ogDescriptionTag) {
      ogDescriptionTag.setAttribute("content", newDescription);
    } else {
      ogDescriptionTag = document.createElement("meta");
      ogDescriptionTag.setAttribute("property", "og:description");
      ogDescriptionTag.setAttribute("content", newDescription);
      document.head.appendChild(ogDescriptionTag);
    }

    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (ogUrlTag) {
      ogUrlTag.setAttribute("content", canonicalUrl);
    } else {
      ogUrlTag = document.createElement("meta");
      ogUrlTag.setAttribute("property", "og:url");
      ogUrlTag.setAttribute("content", canonicalUrl);
      document.head.appendChild(ogUrlTag);
    }

    // TWITTER CARDS
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", newTitle);
    } else {
      twitterTitle = document.createElement("meta");
      twitterTitle.setAttribute("name", "twitter:title");
      twitterTitle.setAttribute("content", newTitle);
      document.head.appendChild(twitterTitle);
    }

    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute("content", newDescription);
    } else {
      twitterDescription = document.createElement("meta");
      twitterDescription.setAttribute("name", "twitter:description");
      twitterDescription.setAttribute("content", newDescription);
      document.head.appendChild(twitterDescription);
    }

  }, [location.pathname]);

  // Schema markup for Portfolio page
  useEffect(() => {
    const schemaScript = document.createElement("script");
    schemaScript.setAttribute("type", "application/ld+json");
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Landscaping Projects Portfolio Vancouver",
      "description": "Portfolio of BUR-HAN's landscaping projects in Vancouver including 3D designs, paving stones, retaining walls, turf installations, and plantings.",
      "url": "https://bur-han.ca/our-projects",
      "provider": {
        "@type": "LocalBusiness",
        "name": "BUR-HAN Garden & Lawn Care",
        "url": "https://bur-han.ca",
        "telephone": "+1-604-983-2687",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2323 Old Dollarton Rd Unit A",
          "addressLocality": "North Vancouver",
          "addressRegion": "BC",
          "postalCode": "V7H 1A9",
          "addressCountry": "CA"
        }
      },
      "hasPart": [
        {
          "@type": "CreativeWork",
          "name": "3D Landscaping Designs",
          "description": "Professional 3D landscaping design portfolio"
        },
        {
          "@type": "CreativeWork",
          "name": "Paving Stone Installations",
          "description": "Paver patio and walkway projects"
        },
        {
          "@type": "CreativeWork",
          "name": "Retaining Wall Projects",
          "description": "Structural and decorative retaining walls"
        },
        {
          "@type": "CreativeWork",
          "name": "Turf Installations",
          "description": "Artificial and natural turf projects"
        },
        {
          "@type": "CreativeWork",
          "name": "Garden Plantings",
          "description": "Residential and commercial planting projects"
        }
      ]
    });
    
    const existingSchema = document.querySelector('script[data-schema="projects-portfolio"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    schemaScript.setAttribute("data-schema", "projects-portfolio");
    document.head.appendChild(schemaScript);
  }, []);

  return (
    <div className="wrapper">
      <Header />
      <div className="projectsHeader">
        {/* H1 - Optimized with primary keyword */}
        <h1 data-aos="zoom-in">Our Landscaping Projects</h1>
      </div>
      <div className="projectsMain" data-aos="fade-up">
        <h2 className="text-center" style={{ padding: "2vw" }}>
          Explore our projects and get inspired for your next outdoor
          transformation.
        </h2>
        <hr />
        <TextCarousel texts={carouselTexts} interval={5000} />

        {/* -------------- background image box -------------*/}
        <div className="projectsBox">
          <div className="row">
            <div className="col-4" data-aos="fade-right">
              <a href="/our-projects/3d-design">
                <div className="box box1">
                  <h4> 3D Design</h4>
                </div>
              </a>
            </div>
            <div className="col-4" data-aos="fade-down">
              <a href="/our-projects/paving-stones">
                <div className="box box2">
                  <h4>Paving Stones</h4>
                </div>
              </a>
            </div>
            <div className="col-4" data-aos="fade-up">
              <a href="/our-projects/retaining-walls">
                <div className="box box3">
                  <h4> Retaining Walls</h4>
                </div>
              </a>
            </div>
            <div className="col-4" data-aos="fade-left">
              <a href="/our-projects/turf-installs">
                <div className="box box4">
                  <h4> Turf Installs</h4>
                </div>
              </a>
            </div>
            <div className="col-4" data-aos="fade-up">
              <a href="/our-projects/plantings">
                <div className="box box6">
                  <h4>Plantings</h4>
                </div>
              </a>
            </div>
            <div className="col-4" data-aos="fade-right">
              <a href="/our-projects/one-time-cleanups">
                <div className="box box5">
                  <h4>One-Time Cleanups</h4>
                </div>
              </a>
            </div>
          </div>
        </div>
        <hr />

        <section className="project-text" data-aos="fade-up">
          <div className="container">
            <h2> Landscaping Projects That Inspire Outdoor Living</h2>
            <p>
              At <strong>BUR-HAN Garden & Lawn Care</strong>, we proudly
              showcase a portfolio of expertly crafted landscaping projects that
              span across Vancouver and surrounding communities. From
              sustainable turf installations to elegant paving stone designs and
              structurally sound retaining walls, every project we undertake
              reflects our passion for outdoor excellence, durability, and
              beauty.
            </p>
            <p>
              Our experienced team of landscape professionals takes pride in
              transforming ordinary spaces into inspiring outdoor environments
              that balance both form and function. We blend innovative 3D design
              capabilities with time-tested construction techniques to deliver
              results that last—and impress.
            </p>
            <p>
              Through our 3D landscaping design services, we help homeowners and
              businesses visualize the transformation of their outdoor spaces
              before any construction begins. These immersive renderings allow
              our clients to make informed decisions and feel confident about
              the final results. Whether it's choosing materials, layouts, or
              plant arrangements, 3D visualization brings clarity and peace of
              mind to every project. This forward-thinking approach is part of
              what sets BUR-HAN apart in the landscaping industry.
            </p>

            <p>
              Explore our featured landscaping projects below to see how
              <strong> BUR-HAN</strong> turns ideas into reality. Whether you're
              looking to add curb appeal, increase property value, or create an
              outdoor sanctuary, we're here to bring your vision to life.
            </p>
          </div>
        </section>

        <div className="secondBanner">
          <div className="container" data-aos="fade-down">
            <div className="row ">
              <div className="col-md-6 ">
                <h3>Ready to elevate your outdoor experience?</h3>
                <p>
                  Fill out our Get a Quote page, and let our expert landscaping
                  team bring your vision to life with a personalized estimate
                  tailored to your unique preferences and needs.
                </p>
              </div>
              <div className="col-md-6 ">
                <div className="call-to-action text-right">
                  <a href="/contact-us" className="get-a-quote">
                    Get A Quote
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* -------------- end -------------*/}
      </div>
      <Footer />
    </div>
  );
}
